// Bootstrap: sync layer between localStorage (what the mockup uses) and the API.
//
// Strategy — "write-through cache":
//   1. init() runs BEFORE the mockup code. It fetches everything from the API
//      and populates localStorage. The mockup then reads localStorage as usual.
//   2. installWriteThrough() monkey-patches localStorage.setItem/removeItem so
//      every apb-cc-* write also flushes to the API in the background (debounced
//      per key). Mockup code stays synchronous; the API sync is fire-and-forget.
//   3. migrateIfNeeded() runs once per browser: if there's local data that isn't
//      on the server yet (returning user with pre-existing state), it pushes it
//      up before hydrating.
//
// LocalStorage keys we manage:
//   apb-cc-notes                → notes table (list)
//   apb-cc-custom-panels        → custom_panels table (list)
//   apb-cc-custom-quadrants     → custom_quadrants table (list)
//   Everything else that starts with apb-cc-*  → user_prefs (KV)

import { api } from './api.js';

const MIGRATED_FLAG = 'apb-cc-migrated';

// Keys that map to structured tables, not user_prefs.
const LIST_KEYS = new Set([
  'apb-cc-notes',
  'apb-cc-custom-panels',
  'apb-cc-custom-quadrants',
]);

/** Public: call once before the mockup script runs. */
export async function init() {
  // Try to reach the API. If offline, skip sync entirely and let the mockup
  // run against local storage as before — no crash, just no cross-device sync.
  let reachable = false;
  try {
    await api.health();
    reachable = true;
  } catch (err) {
    console.warn('[bootstrap] API unreachable, running in offline mode', err);
  }

  if (reachable) {
    try {
      await migrateIfNeeded();
      await hydrateFromApi();
    } catch (err) {
      console.error('[bootstrap] hydrate/migrate failed', err);
    }
    installWriteThrough();
  }
}

/**
 * First-time migration. If localStorage has apb-cc-* data AND the server is
 * empty (i.e. fresh Worker/D1 with no rows), push local up.
 *
 * Idempotent — we set MIGRATED_FLAG once done. Safe to re-run: if the flag is
 * set, we skip entirely.
 */
async function migrateIfNeeded() {
  if (localStorage.getItem(MIGRATED_FLAG) === '1') return;

  // If the server already has data, don't migrate — treat this browser as a
  // fresh device syncing DOWN from the server. (This handles second-device login.)
  const [serverPrefs, serverPanels, serverQuads, serverNotes] = await Promise.all([
    api.listPrefs(),
    api.listPanels(),
    api.listQuadrants(),
    api.listNotes(),
  ]);
  const serverHasData =
    Object.keys(serverPrefs).length > 0 ||
    serverPanels.length > 0 ||
    serverQuads.length > 0 ||
    serverNotes.length > 0;

  if (serverHasData) {
    localStorage.setItem(MIGRATED_FLAG, '1');
    return;
  }

  const jobs = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith('apb-cc-') || key === MIGRATED_FLAG) continue;
    const raw = localStorage.getItem(key);
    if (raw == null) continue;

    if (key === 'apb-cc-notes') {
      let notes = [];
      try { notes = JSON.parse(raw) || []; } catch {}
      for (const n of notes) {
        jobs.push(
          api.createNote({ id: String(n.id ?? crypto.randomUUID()), text: n.text ?? '', ts: n.ts ?? '' }),
        );
      }
    } else if (key === 'apb-cc-custom-panels') {
      let panels = [];
      try { panels = JSON.parse(raw) || []; } catch {}
      for (const p of panels) {
        jobs.push(
          api.createPanel({
            id: p.id,
            quadrant_id: p.quadrantId ?? p.quadrant_id,
            title: p.title,
            content: p.content ?? null,
            url: p.url ?? null,
            tint: p.tint ?? null,
            sort_order: p.sort_order ?? null,
          }),
        );
      }
    } else if (key === 'apb-cc-custom-quadrants') {
      let quads = [];
      try { quads = JSON.parse(raw) || []; } catch {}
      for (const q of quads) {
        jobs.push(
          api.createQuadrant({
            id: q.id,
            code: q.code,
            name: q.name ?? null,
            sort_order: q.sort_order ?? null,
          }),
        );
      }
    } else {
      // Simple KV — everything else goes into user_prefs.
      let value;
      try { value = JSON.parse(raw); } catch { value = raw; }
      jobs.push(api.putPref(key, value));
    }
  }

  const results = await Promise.allSettled(jobs);
  const failed = results.filter((r) => r.status === 'rejected');
  if (failed.length > 0) {
    console.warn('[bootstrap] some migrations failed', failed);
    return; // don't set flag — we'll try again next load
  }
  localStorage.setItem(MIGRATED_FLAG, '1');
  console.info('[bootstrap] migrated %d local items to server', jobs.length);
}

/**
 * Pull server state DOWN into localStorage. Runs on every load, so the app
 * is instantly cross-device.
 */
async function hydrateFromApi() {
  const [prefs, panels, quads, notes] = await Promise.all([
    api.listPrefs(),
    api.listPanels(),
    api.listQuadrants(),
    api.listNotes(),
  ]);

  // Simple KV prefs
  for (const [key, value] of Object.entries(prefs)) {
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
  }

  // Custom panels — server shape uses snake_case; mockup expects camelCase quadrantId
  const panelsForMockup = panels.map((p) => ({
    id: p.id,
    quadrantId: p.quadrant_id,
    title: p.title,
    content: p.content ?? '',
    url: p.url ?? '',
    tint: p.tint ?? '',
    createdAt: (p.created_at ?? 0) * 1000,
  }));
  localStorage.setItem('apb-cc-custom-panels', JSON.stringify(panelsForMockup));

  const quadsForMockup = quads.map((q) => ({
    id: q.id,
    code: q.code,
    name: q.name ?? '',
    createdAt: (q.created_at ?? 0) * 1000,
  }));
  localStorage.setItem('apb-cc-custom-quadrants', JSON.stringify(quadsForMockup));

  const notesForMockup = notes.map((n) => ({
    id: n.id,
    text: n.text,
    ts: n.ts,
  }));
  localStorage.setItem('apb-cc-notes', JSON.stringify(notesForMockup));
}

/**
 * Monkey-patch localStorage.setItem so any write to an apb-cc-* key also
 * flushes to the API in the background. Debounced per key so rapid writes
 * (drag reorders, typing in scratchpad) collapse to one request.
 */
function installWriteThrough() {
  const origSetItem = localStorage.setItem.bind(localStorage);
  const origRemoveItem = localStorage.removeItem.bind(localStorage);

  const timers = new Map();
  const flush = (key) => {
    clearTimeout(timers.get(key));
    timers.set(
      key,
      setTimeout(() => syncKey(key), 250),
    );
  };

  localStorage.setItem = function (key, value) {
    origSetItem(key, value);
    if (typeof key === 'string' && key.startsWith('apb-cc-') && key !== MIGRATED_FLAG) {
      flush(key);
    }
  };

  localStorage.removeItem = function (key) {
    origRemoveItem(key);
    if (typeof key === 'string' && key.startsWith('apb-cc-') && key !== MIGRATED_FLAG) {
      flush(key);
    }
  };
}

/**
 * Push the current value of one localStorage key to the server.
 *
 * For KV keys (user_prefs), we just PUT the value.
 * For list keys, we compute a diff against what the server has and
 * create/update/delete individual rows to keep the tables normalized.
 *
 * List-key sync uses full-replace semantics for simplicity — read what's
 * on the server, diff with local, apply the difference. Racy across
 * concurrent devices but fine for single-user MVP.
 */
async function syncKey(key) {
  try {
    const raw = localStorage.getItem(key);

    if (!LIST_KEYS.has(key)) {
      // Simple KV — one PUT
      let value;
      try { value = raw == null ? null : JSON.parse(raw); } catch { value = raw; }
      if (value === null) {
        await api.deletePref(key);
      } else {
        await api.putPref(key, value);
      }
      return;
    }

    let localList = [];
    try { localList = raw == null ? [] : JSON.parse(raw) || []; } catch {}

    if (key === 'apb-cc-notes') {
      const server = await api.listNotes();
      const serverIds = new Set(server.map((n) => String(n.id)));
      const localIds = new Set(localList.map((n) => String(n.id)));
      const toCreate = localList.filter((n) => !serverIds.has(String(n.id)));
      const toDelete = server.filter((n) => !localIds.has(String(n.id)));
      await Promise.all([
        ...toCreate.map((n) =>
          api.createNote({ id: String(n.id), text: n.text ?? '', ts: n.ts ?? '' }),
        ),
        ...toDelete.map((n) => api.deleteNote(n.id)),
      ]);
    } else if (key === 'apb-cc-custom-panels') {
      const server = await api.listPanels();
      const serverIds = new Set(server.map((p) => p.id));
      const localIds = new Set(localList.map((p) => p.id));
      const toCreate = localList.filter((p) => !serverIds.has(p.id));
      const toDelete = server.filter((p) => !localIds.has(p.id));
      const toUpdate = localList.filter((p) => {
        const s = server.find((x) => x.id === p.id);
        if (!s) return false;
        return (
          s.title !== p.title ||
          s.content !== (p.content ?? null) ||
          s.url !== (p.url ?? null) ||
          s.tint !== (p.tint ?? null) ||
          s.quadrant_id !== (p.quadrantId ?? p.quadrant_id)
        );
      });
      await Promise.all([
        ...toCreate.map((p) =>
          api.createPanel({
            id: p.id,
            quadrant_id: p.quadrantId ?? p.quadrant_id,
            title: p.title,
            content: p.content ?? null,
            url: p.url ?? null,
            tint: p.tint ?? null,
            sort_order: p.sort_order ?? null,
          }),
        ),
        ...toUpdate.map((p) =>
          api.updatePanel(p.id, {
            quadrant_id: p.quadrantId ?? p.quadrant_id,
            title: p.title,
            content: p.content ?? null,
            url: p.url ?? null,
            tint: p.tint ?? null,
          }),
        ),
        ...toDelete.map((p) => api.deletePanel(p.id)),
      ]);
    } else if (key === 'apb-cc-custom-quadrants') {
      const server = await api.listQuadrants();
      const serverIds = new Set(server.map((q) => q.id));
      const localIds = new Set(localList.map((q) => q.id));
      const toCreate = localList.filter((q) => !serverIds.has(q.id));
      const toDelete = server.filter((q) => !localIds.has(q.id));
      const toUpdate = localList.filter((q) => {
        const s = server.find((x) => x.id === q.id);
        if (!s) return false;
        return s.code !== q.code || s.name !== (q.name ?? null);
      });
      await Promise.all([
        ...toCreate.map((q) =>
          api.createQuadrant({
            id: q.id,
            code: q.code,
            name: q.name ?? null,
            sort_order: q.sort_order ?? null,
          }),
        ),
        ...toUpdate.map((q) =>
          api.updateQuadrant(q.id, { code: q.code, name: q.name ?? null }),
        ),
        ...toDelete.map((q) => api.deleteQuadrant(q.id)),
      ]);
    }
  } catch (err) {
    console.warn('[bootstrap] sync failed for', key, err);
  }
}
